import {
  CommandBarItem,
  compactCommands,
  createCommand,
  navigateCommand,
  RenameShareModal,
  routes,
} from '@sketch/modules-common'
import { ShowModal } from '@sketch/components'
import { History } from 'history'
import { ShareInfoFragment } from '@sketch/gql-types'
import copy from 'copy-to-clipboard'

import { PartialShareModalCreate } from '../modals/PartialShareModal'
import SharingModal from '../modals/SharingModal'
import {
  ArchiveStorageItemsModal,
  MoveStorageItemModal,
} from '../../storageItems'
import { ExportDesignTokensModal } from '../modals/ExportDesignTokensModal'
import { DeleteShareModal } from '../components/DeleteShareModal'
import { DocumentComponentsStateExported } from '../components/ComponentsStateContext'

import { SegmentValues } from '../constants'

import { ReactComponent as ComponentsIcon } from '@sketch/icons/misc-components-view-17'
import { ReactComponent as PrototypesIcon } from '@sketch/icons/prototype-17'
import { ReactComponent as PreviewsIcon } from '@sketch/icons/document-magnifying-glass-17'
import { ReactComponent as RenameIcon } from '@sketch/icons/text-edit-17'
import { ReactComponent as VersionsIcon } from '@sketch/icons/clock-rewind-24'
import { ReactComponent as InspectIcon } from '@sketch/icons/code-block-17'
import { ReactComponent as InfoIcon } from '@sketch/icons/circle-info-17'
import {
  ReactComponent as CommentsIcon,
  ReactComponent as ShowCommentIcon,
} from '@sketch/icons/comment-chat-bubble-17'
import { ReactComponent as MoveIcon } from '@sketch/icons/img-arrows-right-left-17'
import { ReactComponent as ArchiveIcon } from '@sketch/icons/box-17'
import { ReactComponent as CopyLinkIcon } from '@sketch/icons/markdown-link-share-17'
import { ReactComponent as SettingsIcon } from '@sketch/icons/gear-small-17'
import { ReactComponent as ShareIcon } from '@sketch/icons/persons-2-17'
import { ReactComponent as TokensIcon } from '@sketch/icons/token-17'
import { ReactComponent as TrashIcon } from '@sketch/icons/trash-empty-17'
import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'
import { ReactComponent as FollowIcon } from '@sketch/icons/misc-notification-17'
import { ReactComponent as UnFollowIcon } from '@sketch/icons/misc-notification-slash-17'
import { ReactComponent as DuplicateIcon } from '@sketch/icons/rectangles-2-17'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-17'
import { ReactComponent as EditIcon } from '@sketch/icons/pen-nib-17'

interface CurrentDocumentNavigationDefinition {
  analyticsId: string
  icon: CommandBarItem['icon']
  id: string
  keywords: readonly string[]
  priority: number
  title: string
  to:
    | typeof routes.SHARE_PROTOTYPES
    | typeof routes.CWV_SYMBOLS
    | typeof routes.CWV_TEXT_STYLES
    | typeof routes.CWV_LAYER_STYLES
    | typeof routes.CWV_COLOR_VARIABLES
}

const currentDocumentNavigationDefinitions: readonly CurrentDocumentNavigationDefinition[] =
  [
    {
      id: 'document.go-to-prototypes',
      analyticsId: 'document.go-to-prototypes',
      title: 'Go to Prototypes',
      icon: PrototypesIcon,
      keywords: ['prototype', 'player', 'flows'],
      priority: 20,
      to: routes.SHARE_PROTOTYPES,
    },
    {
      id: 'document.go-to-symbols',
      analyticsId: 'document.go-to-symbols',
      title: 'Go to Symbols',
      icon: ComponentsIcon,
      keywords: ['components'],
      priority: 18,
      to: routes.CWV_SYMBOLS,
    },
    {
      id: 'document.go-to-text-styles',
      analyticsId: 'document.go-to-text-styles',
      title: 'Go to Text Styles',
      icon: ComponentsIcon,
      keywords: ['components'],
      priority: 17,
      to: routes.CWV_TEXT_STYLES,
    },
    {
      id: 'document.go-to-layer-styles',
      analyticsId: 'document.go-to-layer-styles',
      title: 'Go to Layer Styles',
      icon: ComponentsIcon,
      keywords: ['components'],
      priority: 16,
      to: routes.CWV_LAYER_STYLES,
    },
    {
      id: 'document.go-to-color-variables',
      analyticsId: 'document.go-to-color-variables',
      title: 'Go to Color Variables',
      icon: ComponentsIcon,
      keywords: ['components', 'tokens'],
      priority: 16,
      to: routes.CWV_COLOR_VARIABLES,
    },
  ] as const

const createCanvasCommands = ({
  share,
  sidebarTabToggle,
}: Pick<CreateCurrentDocumentCommandsProps, 'share'> & {
  sidebarTabToggle: (hash?: SegmentValues) => void
}): CommandBarItem[] => {
  if (!share) {
    return []
  }

  return compactCommands([
    createCommand(
      {
        id: 'document.go-to-details',
        analyticsId: 'document.go-to-details',
        title: 'Show Document Details',
        subtitle: 'Toggle the document details panel',
        shortcut: 'D',
        section: 'Current Document',
        keywords: ['details'],
        icon: InfoIcon,
        priority: 25,
      },
      () => sidebarTabToggle('About')
    ),
    createCommand(
      {
        id: 'document.go-to-inspect',
        analyticsId: 'document.go-to-inspect',
        title: 'Inspect',
        subtitle: 'Toggle inspect mode',
        shortcut: 'I',
        section: 'Current Document',
        keywords: ['inspect'],
        icon: InspectIcon,
        priority: 25,
      },
      () => sidebarTabToggle('Inspect')
    ),
    createCommand(
      {
        id: 'document.go-to-versions',
        analyticsId: 'document.go-to-verions',
        title: 'Show Versions',
        subtitle: 'Toggle version history mode',
        shortcut: 'V',
        section: 'Current Document',
        keywords: ['history', 'version'],
        icon: VersionsIcon,
        priority: 25,
      },
      () => sidebarTabToggle('Version')
    ),
    createCommand(
      {
        id: 'document.go-to-comments',
        analyticsId: 'document.go-to-comments',
        title: 'Show Comments',
        subtitle: 'Toggle comment mode',
        shortcut: 'C',
        section: 'Current Document',
        keywords: ['annotation', 'comment'],
        icon: CommentsIcon,
        priority: 25,
      },
      () => sidebarTabToggle('Comment')
    ),
  ])
}

interface CreateCurrentDocumentCommandsProps {
  canDownload: boolean
  canOpenInSketch: boolean
  componentsState: DocumentComponentsStateExported
  duplicateToDraftsTitle?: string
  history: History
  isActive: boolean
  isCommentsHidden: boolean
  isTemplate: boolean
  openDocumentInfoModal: () => void
  onDownload: () => void
  onDuplicateToDrafts?: () => void
  onDuplicateToSameLocation?: () => void
  onOpenInSketch: () => void
  onToggleCommentsHidden: () => void
  onTogglePin: () => void
  onToggleSubscription: () => void
  share?: ShareInfoFragment
  showArchive: boolean
  showModal: ShowModal
  showToast: (message: string) => void
  sidebarTabToggle: (hash?: SegmentValues) => void
  versions: {
    current: { shortId: string }
    latest: { shortId: string }
  }
  workspace?: ShareInfoFragment['workspace']
}

export const createCurrentDocumentCommands = ({
  canDownload,
  canOpenInSketch,
  componentsState,
  duplicateToDraftsTitle,
  history,
  isActive,
  isCommentsHidden,
  isTemplate,
  openDocumentInfoModal,
  onDownload,
  onDuplicateToDrafts,
  onDuplicateToSameLocation,
  onOpenInSketch,
  onToggleCommentsHidden,
  onTogglePin,
  onToggleSubscription,
  workspace,
  share,
  showArchive,
  showModal,
  showToast,
  sidebarTabToggle,
  versions,
}: CreateCurrentDocumentCommandsProps): CommandBarItem[] => {
  if (!share) {
    return []
  }

  const workspaceIdentifier = workspace?.identifier
  const isArchived = !!share.archivedAt
  const isFollowingComments = share.subscriptionStatus === 'ON'

  const modalCommands = compactCommands([
    canOpenInSketch &&
      createCommand(
        {
          id: 'document.open-in-sketch',
          analyticsId: 'document.open-in-sketch',
          title: isTemplate ? 'New from Template' : 'Edit in Sketch',
          section: 'Current Document',
          keywords: ['open', 'edit', 'sketch'],
          icon: EditIcon,
          priority: 16,
        },
        onOpenInSketch
      ),
    canDownload &&
      createCommand(
        {
          id: 'document.download',
          analyticsId: 'document.download',
          title: 'Download',
          section: 'Current Document',
          keywords: ['download', 'save'],
          icon: DownloadIcon,
          priority: 16,
        },
        onDownload
      ),
    onDuplicateToSameLocation &&
      createCommand(
        {
          id: 'document.duplicate',
          analyticsId: 'document.duplicate',
          title: 'Duplicate',
          section: 'Current Document',
          keywords: ['duplicate', 'copy'],
          icon: DuplicateIcon,
          priority: 16,
        },
        onDuplicateToSameLocation
      ),
    onDuplicateToDrafts &&
      createCommand(
        {
          id: 'document.duplicate-to-drafts',
          analyticsId: 'document.duplicate-to-drafts',
          title: duplicateToDraftsTitle || 'Duplicate to My Drafts',
          section: 'Current Document',
          keywords: ['duplicate', 'copy', 'drafts'],
          icon: DuplicateIcon,
          priority: 16,
        },
        onDuplicateToDrafts
      ),
    share.userCanUpdateSettings &&
      createCommand(
        {
          id: 'document.settings',
          analyticsId: 'document.settings',
          title: 'Settings…',
          section: 'Current Document',
          keywords: ['document', 'settings', 'info'],
          icon: SettingsIcon,
          priority: 15,
        },
        openDocumentInfoModal
      ),
    share.userCanUpdateSettings &&
      createCommand(
        {
          id: 'document.edit-permissions',
          analyticsId: 'document.edit-permissions',
          title: 'Edit Permissions…',
          section: 'Current Document',
          keywords: ['share', 'permission', 'public'],
          icon: ShareIcon,
          priority: 15,
        },
        () =>
          showModal(SharingModal, {
            identifier: share.identifier,
            name: share.name,
          })
      ),
    createCommand(
      {
        id: 'document.copy-link',
        analyticsId: 'document.copy-link',
        title: 'Copy Link',
        section: 'Current Document',
        keywords: ['copy', 'link', 'url', 'share'],
        icon: CopyLinkIcon,
        priority: 15,
      },
      () => {
        copy(
          `${window.location.origin}${routes.SHARE_VIEW.create({
            shareID: share.identifier,
          })}`
        )
        showToast('Link copied')
      }
    ),
    isActive &&
      createCommand(
        {
          id: 'document.toggle-pin',
          analyticsId: share.pinnedByCurrentUserAt
            ? 'document.unpin'
            : 'document.pin',
          title: share.pinnedByCurrentUserAt
            ? 'Unpin Document'
            : 'Pin Document',
          section: 'Current Document',
          keywords: ['pin', 'pinned'],
          icon: PinIcon,
          priority: 12,
        },
        onTogglePin
      ),
    share.commentsEnabled &&
      createCommand(
        {
          id: 'document.toggle-comments-visibility',
          analyticsId: isCommentsHidden
            ? 'document.show-comments'
            : 'document.hide-comments',
          title: isCommentsHidden ? 'Show Comments' : 'Hide Comments',
          section: 'Current Document',
          keywords: ['comments', 'annotations', 'hide', 'show'],
          shortcut: 'Shift N',
          icon: ShowCommentIcon,
          priority: 12,
        },
        onToggleCommentsHidden
      ),
    share.commentsEnabled &&
      createCommand(
        {
          id: 'document.toggle-comment-subscription',
          analyticsId: isFollowingComments
            ? 'document.unfollow-comments'
            : 'document.follow-comments',
          title: isFollowingComments ? 'Unfollow Comments' : 'Follow Comments',
          section: 'Current Document',
          keywords: ['comments', 'notifications', 'follow', 'unfollow'],
          icon: isFollowingComments ? UnFollowIcon : FollowIcon,
          priority: 12,
        },
        onToggleSubscription
      ),
    share.userCanUpdateSettings &&
      share.version &&
      createCommand(
        {
          id: 'document.export-design-tokens',
          analyticsId: 'document.export-design-tokens',
          title: 'Export Design Tokens…',
          section: 'Current Document',
          keywords: ['export', 'tokens', 'design tokens'],
          icon: TokensIcon,
          priority: 10,
        },
        () =>
          showModal(ExportDesignTokensModal, {
            shareIdentifier: share.identifier,
            versionShortId: versions.current.shortId,
            userCanEditExportUrl: share.userCanUpdateSettings,
            componentsState,
          })
      ),
    share.userCanUpdateSettings &&
      createCommand(
        {
          id: 'document.rename',
          analyticsId: 'document.rename',
          title: 'Rename…',
          section: 'Current Document',
          keywords: ['rename', 'name'],
          icon: RenameIcon,
          priority: 10,
        },
        () =>
          showModal(RenameShareModal, {
            id: share.identifier,
            name: share.name,
          })
      ),
    workspaceIdentifier
      ? share.userCanUpdateSettings &&
        createCommand(
          {
            id: 'document.move',
            analyticsId: 'document.move',
            title: isArchived ? 'Unarchive…' : 'Move To…',
            section: 'Current Document',
            keywords: isArchived
              ? ['archive', 'unarchive', 'restore']
              : ['transfer', 'move'],
            icon: isArchived ? ArchiveIcon : MoveIcon,
            priority: 10,
          },
          () =>
            showModal(MoveStorageItemModal, {
              items: [share],
              workspaceIdentifier,
              parentProjectIdentifier: share.project?.identifier || null,
              originType:
                share.archivedAt || share.deletedAt ? 'stashed' : 'standard',
              action: isArchived ? 'unarchive' : 'move',
            })
        )
      : false,
    workspaceIdentifier
      ? share.userCanUpdateSettings &&
        !isArchived &&
        showArchive &&
        createCommand(
          {
            id: 'document.archive',
            analyticsId: 'document.archive',
            title: 'Archive…',
            section: 'Current Document',
            keywords: ['archive'],
            icon: ArchiveIcon,
            priority: 10,
          },
          () => showModal(ArchiveStorageItemsModal, { items: [share] })
        )
      : false,
    workspaceIdentifier
      ? share.userCanUpdateSettings &&
        createCommand(
          {
            id: 'document.delete',
            analyticsId: 'document.delete',
            title: 'Delete…',
            section: 'Current Document',
            keywords: ['delete', 'trash', 'remove'],
            icon: TrashIcon,
            priority: 10,
          },
          () =>
            showModal(DeleteShareModal, {
              name: share.name,
              shareShortId: share.identifier,
              workspaceId: workspaceIdentifier,
              projectId: share.project?.identifier,
            })
        )
      : false,
    share.version &&
      createCommand(
        {
          id: 'document.create-preview',
          analyticsId: 'document.create-preview',
          title: 'Create a Preview Link…',
          section: 'Current Document',
          keywords: ['share', 'preview', 'link'],
          icon: PreviewsIcon,
          priority: 15,
        },
        () =>
          showModal(PartialShareModalCreate, {
            shareIdentifier: share.identifier,
            shareName: share.name,
            versionShortId: share.version!.shortId,
          })
      ),
  ])

  const navigationCommands = currentDocumentNavigationDefinitions.map(item =>
    navigateCommand({
      id: item.id,
      analyticsId: item.analyticsId,
      title: item.title,
      section: 'Current Document',
      keywords: [...item.keywords],
      icon: item.icon,
      priority: item.priority,
      to: item.to.create({ shareID: share.identifier }),
      history,
    })
  )

  return [
    ...createCanvasCommands({ share, sidebarTabToggle }),
    ...modalCommands,
    ...navigationCommands,
  ]
}
